import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import Swal from 'sweetalert2';
import { Icon } from '@iconify/react';
import { Modal, Button, Form, Row, Col, Badge, Card, Tab, Nav, Dropdown, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import VisitControlModal from '../components/VisitControlModal';
import InvoiceDetailModal from '../components/InvoiceDetailModal';
import PatientInvoiceDetailModal from '../components/PatientInvoiceDetailModal';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { useAuth } from '../context/AuthContext';

const AssistantDashboard = () => {
    // Layout State
    const [sidebarActive, setSidebarActive] = useState(false);
    const [mobileMenu, setMobileMenu] = useState(false);
    const [activeTab, setActiveTab] = useState('queue');
    const location = useLocation();

    // Tab Sync Effect
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const view = params.get('view');
        if (view === 'settings') setActiveTab('settings');
        else if (view === 'appointments') setActiveTab('appointments');
        else if (view === 'mr_visits') setActiveTab('mr_visits');
        else if (view === 'invoices') setActiveTab('invoices');
        else setActiveTab('queue');
    }, [location.search]);

    // Auth Data
    const { user } = useAuth();

    // Data State
    const [doctors, setDoctors] = useState([]);
    const [appointments, setAppointments] = useState([]);
    const [mrVisits, setMrVisits] = useState([]);
    const [queue, setQueue] = useState([]); // Today's active queue
    const [queueStatus, setQueueStatus] = useState(null); // Queue Pause Status
    const [loading, setLoading] = useState(true);

    const [profileData, setProfileData] = useState({ name: '', phone: '' });
    // Emergency Ref to stop loops
    const prevEmergencyMode = React.useRef(false);
    // Custom Toggle for Action Dropdown
    const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
        <a
            href=""
            ref={ref}
            onClick={(e) => {
                e.preventDefault();
                onClick(e);
            }}
            className="text-secondary p-2 rounded-circle hover-bg-light d-inline-block"
        >
            <Icon icon="solar:menu-dots-bold" className="text-xl" />
            {children}
        </a>
    ));
    const [passwords, setPasswords] = useState({ new: '', confirm: '' });

    // Filters
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [selectedDoctor, setSelectedDoctor] = useState(''); // Doctor ID

    // Booking Modal
    const [showBooking, setShowBooking] = useState(false);
    const [bookingData, setBookingData] = useState({
        patientName: '',
        patientPhone: '',
        patientEmail: '', // Added Email
        doctorId: '',
        timeSlot: '',
        type: 'Physical',
        price: '',
        bookingMode: 'Manual', // Verified 'Manual' or 'Online'
        date: new Date() // Default to today
    });
    const [bookingAppointments, setBookingAppointments] = useState([]); // For checking availability in modal

    // Emergency State
    const [emergencyMode, setEmergencyMode] = useState(false);

    // MR Visit State
    const [showMRModal, setShowMRModal] = useState(false);
    const [mrData, setMrData] = useState({ mrName: '', email: '', companyName: '', doctorId: '', purpose: '' });

    // INVOICES STATE
    const [invoices, setInvoices] = useState([]);
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [invoiceFilters, setInvoiceFilters] = useState({ search: '', date: '' });

    // PENDING PAYMENTS STATE
    const [pendingPayments, setPendingPayments] = useState([]);

    // VITALS STATE
    const [showVitalsModal, setShowVitalsModal] = useState(false);
    const [vitalsData, setVitalsData] = useState({
        patientId: '',
        appointmentId: '',
        bp: '',
        temperature: '',
        pulse: '',
        weight: '',
        spo2: '',
        notes: ''
    });


    // Visit Control
    const [showVisitModal, setShowVisitModal] = useState(false);
    const [activeVisit, setActiveVisit] = useState(null);

    // DETAILS MODAL STATE
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [detailsData, setDetailsData] = useState({
        appointment: null,
        analysis: null,
        prescription: null,
        prescription: null,
        loading: false
    });

    // CANCELLATION DETAILS STATE
    const [showCancellationModal, setShowCancellationModal] = useState(false);
    const [cancellationData, setCancellationData] = useState(null);



    // Constants
    const TIME_SLOTS = [
        "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
        "12:00", "12:30", "14:00", "14:30", "15:00", "15:30",
        "16:00", "16:30", "17:00", "17:30"
    ];

    useEffect(() => {
        fetchDoctors();
    }, []);

    useEffect(() => {
        if (selectedDoctor) {
            fetchAppointments();
        }
    }, [selectedDate, selectedDoctor]);

    const toggleSidebar = () => setSidebarActive(!sidebarActive);
    const mobileMenuControl = () => setMobileMenu(!mobileMenu);
    const closeSidebar = () => setMobileMenu(false);

    // API Calls
    const fetchDoctors = async () => {
        try {
            const res = await api.get('/clinic/staff');
            console.log("Raw Staff Response:", res.data); // Debug
            const docs = res.data.filter(u => u.role === 'Doctor');
            console.log("Filtered Doctors:", docs); // Debug
            setDoctors(docs);
            if (docs.length > 0) setSelectedDoctor(docs[0]._id);
        } catch (error) {
            console.error("Fetch Doctors Failed:", error);
        }
    };

    const formatDateLocal = (date) => {
        if (!date) return '';
        const d = new Date(date);
        const year = d.getFullYear();
        const month = (d.getMonth() + 1).toString().padStart(2, '0');
        const day = d.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const fetchAppointments = async () => {
        setLoading(true);
        try {
            const res = await api.get('/appointments', {
                params: {
                    date: selectedDate, // Fetch specifically for this date to support rescheduling/viewing
                    doctorId: selectedDoctor
                }
            });
            setAppointments(res.data.sort((a, b) => a.queueNumber - b.queueNumber));

            // Filter for Queue Tab (Today's Active/Upcoming)
            const today = new Date().toDateString();
            if (new Date(selectedDate).toDateString() === today) {
                setQueue(res.data.filter(a => ['Upcoming', 'Active'].includes(a.status)).sort((a, b) => a.queueNumber - b.queueNumber));
            } else {
                setQueue([]);
            }
            setLoading(false);
        } catch (error) {
            console.error(error);
            setLoading(false);
        }
    };

    const fetchMRVisits = async () => {
        setLoading(true);
        try {
            const res = await api.get('/mr-visits', {
                params: {
                    date: formatDateLocal(selectedDate),
                    doctorId: selectedDoctor
                }
            });
            setMrVisits(res.data);
            setLoading(false);
        } catch (error) {
            console.error(error);
            setLoading(false);
        }
    };

    const fetchPendingPayments = async () => {
        setLoading(true);
        try {
            const res = await api.get('/assistant/pending-payments');
            setPendingPayments(res.data);
            setLoading(false);
        } catch (error) {
            console.error("Fetch Pending Payments Failed", error);
            setLoading(false);
        }
    };





    // Master Fetch Effect
    useEffect(() => {
        if (activeTab === 'invoices') {
            fetchInvoices(); // Invoices can be clinic-wide, ignore doctor selection if needed
        } else if (activeTab === 'pending_payments') {
            fetchPendingPayments();
        } else if (selectedDoctor) {
            if (activeTab === 'mr_visits') {
                fetchMRVisits();
            } else if (['queue', 'appointments', 'list'].includes(activeTab) || activeTab === 'dashboard') {
                fetchAppointments(); // Default or explicit appt tabs
            }
        }
    }, [selectedDate, selectedDoctor, activeTab]);

    // Booking Availability Fetcher
    useEffect(() => {
        if (showBooking && bookingData.doctorId) {
            const fetchAvailability = async () => {
                try {
                    const token = localStorage.getItem('token');
                    let dateStr;
                    try {
                        const d = bookingData.date instanceof Date ? bookingData.date : new Date(bookingData.date);
                        // Check if valid date
                        if (isNaN(d.getTime())) {
                            // Invalid date, skip or don't send params
                            return;
                        }
                        dateStr = d.toISOString();
                    } catch (err) {
                        return; // Invalid date
                    }

                    const res = await api.get('/appointments', {
                        params: {
                            date: dateStr,
                            doctorId: bookingData.doctorId
                        }
                    });
                    setBookingAppointments(res.data);
                } catch (e) {
                    console.error("Availability Check Failed", e);
                }
            };
            fetchAvailability();
        }
    }, [showBooking, bookingData.date, bookingData.doctorId]);

    // Booking Logic
    const handleBooking = async (e) => {
        e.preventDefault();

        // Show Loading
        Swal.fire({
            title: 'Processing Booking',
            text: 'Creating appointment and generating invoice...',
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        try {
            // Check if patient exists? For MVP walk-in, we use guest fields
            await api.post('/appointments/book', {
                doctorId: bookingData.doctorId || selectedDoctor,
                date: bookingData.date,
                timeSlot: bookingData.timeSlot,
                type: bookingData.type,
                guestName: bookingData.patientName,
                guestPhone: bookingData.patientPhone,
                guestEmail: bookingData.patientEmail,
                price: bookingData.price,
                bookingMode: bookingData.bookingMode
            });

            if (bookingData.bookingMode === 'Online') {
                Swal.fire('Payment Link Sent', 'Link sent to patient email. Appointment pending payment.', 'info');
            } else {
                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: 'Appointment Booked & Invoice Created Successfully!',
                    timer: 3000
                });
            }

            setShowBooking(false);

            // Auto-Activate UI if it was an Emergency booking
            if (bookingData.type === 'Emergency') {
                setEmergencyMode(true);
            }

            fetchAppointments();
            fetchAppointments();
            // Removed duplicate calls
            setBookingData({
                patientName: '', patientPhone: '', patientEmail: '',
                doctorId: '', timeSlot: '', type: 'Physical', price: '', bookingMode: 'Manual', date: new Date()
            });
        } catch (error) {
            Swal.fire('Error', error.response?.data?.message || 'Booking failed', 'error');
        }
    };

    // Queue Action Logic
    // Queue Action Logic
    const updateStatus = async (id, status, missedReason = null) => {
        try {
            // PROMPT FOR REASON IF MISSED
            if (status === 'Missed' && !missedReason) {
                const { value: reason } = await Swal.fire({
                    title: 'Confirm Missed Appointment',
                    text: 'Are you sure you want to mark this appointment as missed?',
                    icon: 'warning',
                    input: 'text',
                    inputLabel: 'Reason for missed appointment',
                    inputPlaceholder: 'e.g., Patient did not show up',
                    showCancelButton: true,
                    confirmButtonText: 'Yes, Mark as Missed',
                    cancelButtonText: 'Cancel',
                    inputValidator: (value) => {
                        if (!value) return 'You need to write a reason to confirm!';
                    }
                });

                if (!reason) return false; // Cancelled
                missedReason = reason;
            }

            await api.put(`/appointments/${id}/status`, { status, missedReason });
            fetchAppointments();
            fetchQueueStatus();

            // Success Feedback
            Swal.fire({
                icon: 'success',
                title: 'Status Updated',
                text: `Appointment marked as ${status}`,
                timer: 2000,
                showConfirmButton: false
            });

            return true;
        } catch (error) {
            console.error("Update Status Error:", error);
            const msg = error.response?.data?.message || 'Failed to update status';
            const detail = error.response?.data?.error || error.message;
            const stack = error.response?.data?.stack; // For console

            if (stack) console.error("Backend Stack:", stack);

            Swal.fire({
                icon: 'error',
                title: 'Update Failed',
                html: `<p>${msg}</p><small class="text-danger">${detail}</small>`
            });
            return false;
        }
    };

    const handleStartVisit = async (appt) => {
        const success = await updateStatus(appt._id, 'Active');
        if (success) {
            setActiveVisit(appt);
            setShowVisitModal(true);

            // NOTIFY DOCTOR
            try {
                await api.post('/assistant/notify-next', { appointmentId: appt._id });
            } catch (error) {
                console.error("Failed to notify doctor:", error);
            }
        }
    };

    const handleOpenVisitModal = (appt) => {
        setActiveVisit(appt);
        setShowVisitModal(true);
    };

    const handleVisitComplete = async (id) => {
        await updateStatus(id, 'Completed');
        // fetchAppointments already called in updateStatus, relying on it there.
        setShowVisitModal(false);
    };

    const handleViewCancellation = (appt) => {
        setCancellationData(appt);
        setShowCancellationModal(true);
    };

    // Conflict Detection Helper
    const checkConflict = (appt) => {
        if (appt.status === 'Completed' || appt.status === 'Missed' || appt.status === 'Cancelled') return false;
        if (appt.isEmergency) return false;

        const doc = doctors.find(d => d._id === appt.doctorId?._id || d._id === appt.doctorId);
        if (!doc || !doc.availability) return false;

        const apptDate = new Date(appt.date);
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const dayName = days[apptDate.getDay()];

        const schedule = doc.availability.find(d =>
            d.day === dayName || d.day.toLowerCase() === dayName.toLowerCase().slice(0, 3)
        );

        // If no schedule found OR isAvailable is false -> Conflict
        if (!schedule || !schedule.isAvailable) return true;

        return false;
    };

    const handleReschedule = (appt) => {
        setBookingData({
            patientName: appt.guestName || appt.patientId?.name,
            patientPhone: appt.guestPhone || appt.patientId?.phone,
            patientEmail: appt.guestEmail || appt.patientId?.email || '',
            doctorId: appt.doctorId?._id || appt.doctorId,
            timeSlot: '', // Let user pick new slot
            type: appt.type,
            date: new Date()
        });
        // We might want to DELETE the old one or UPDATE it?
        // Ideally update. But Booking API is POST.
        // For MVP, simplistic: user books new, manually cancels old.
        // Smart way: Pre-fill booking modal, and on success... wait, booking modal creates NEW.
        // We need 'Reschedule' API or just Cancel Old + Book New logic.
        // I'll show a message "Please cancel old appointment after booking new one" or similar.
        setShowBooking(true);
    };

    // Auto-Refresh Queue (Polling every 5 seconds)
    useEffect(() => {
        const interval = setInterval(() => {
            if (selectedDoctor && ['queue', 'appointments'].includes(activeTab)) {
                // Silent fetch
                const fetchSilent = async () => {
                    try {
                        const res = await api.get('/appointments', {
                            params: {
                                date: selectedDate.toISOString(),
                                doctorId: selectedDoctor
                            }
                        });
                        setAppointments(res.data.sort((a, b) => a.queueNumber - b.queueNumber));
                        // Filter for Queue Tab
                        const today = new Date().toDateString();
                        if (new Date(selectedDate).toDateString() === today) {
                            setQueue(res.data.filter(a => ['Upcoming', 'Active'].includes(a.status)).sort((a, b) => a.queueNumber - b.queueNumber));
                        } else {
                            setQueue([]);
                        }
                    } catch (err) { console.error("Silent Refresh Failed", err); }
                };
                fetchSilent();
            }
        }, 5000);
        return () => clearInterval(interval);
    }, [selectedDoctor, selectedDate, activeTab]);

    const fetchQueueStatus = async () => {
        if (!selectedDoctor) return;
        try {
            const res = await api.get('/queue/status', {
                params: { doctorId: selectedDoctor }
            });
            setQueueStatus(res.data);

            // Sync Emergency Mode from Backend (Strict Source of Truth)
            if (res.data.emergencyMode) {
                // If backend says active, and we haven't alerted yet...
                if (!prevEmergencyMode.current) {
                    setEmergencyMode(true);
                    prevEmergencyMode.current = true; // Mark as alerted

                    Swal.fire({
                        toast: true,
                        position: 'top',
                        icon: 'warning',
                        title: 'EMERGENCY MODE ACTIVE',
                        text: 'A high priority emergency case is in queue.',
                        showConfirmButton: false,
                        timer: 5000,
                        customClass: {
                            title: 'text-danger fw-bold fs-5',
                            popup: 'border border-danger border-2'
                        }
                    });
                }
            } else {
                // Backend says NO active emergency.
                if (prevEmergencyMode.current) {
                    setEmergencyMode(false);
                    prevEmergencyMode.current = false; // Reset alert state

                    Swal.fire({
                        toast: true,
                        position: 'top-end',
                        title: 'Emergency Resolved',
                        text: 'Queue is resuming normal operations.',
                        icon: 'success',
                        showConfirmButton: false,
                        timer: 3000
                    });
                }
            }

        } catch (error) {
            console.error(error);
        }
    };

    // Queue Status Polling
    useEffect(() => {
        let interval;
        if (selectedDoctor && activeTab === 'queue') {
            fetchQueueStatus();
            interval = setInterval(fetchQueueStatus, 5000);
        }
        return () => clearInterval(interval);
    }, [selectedDoctor, activeTab]);


    const fetchInvoices = async () => {
        setLoading(true);
        try {
            const params = {
                search: invoiceFilters.search,
                date: formatDateLocal(selectedDate)
            };
            const res = await api.get('/assistant/my-invoices', { params });
            setInvoices(res.data);
            console.log("Fetched Invoices:", res.data); // Debug for User
            setLoading(false);
        } catch (error) {
            console.error("Fetch Invoices Failed:", error);
            setLoading(false);
        }
    };



    const handleEmailInvoice = async (id) => {
        const { value: email } = await Swal.fire({
            title: 'Email Invoice',
            input: 'email',
            inputLabel: 'Enter recipient email address',
            inputPlaceholder: 'patient@example.com',
            showCancelButton: true,
            confirmButtonText: 'Send Email',
            showLoaderOnConfirm: true,
            preConfirm: async (email) => {
                try {
                    await api.post(`/clinic/invoices/${id}/email`, { targetEmail: email });
                    Swal.fire('Sent!', 'Invoice has been emailed.', 'success');
                } catch (error) {
                    Swal.showValidationMessage(`Request failed: ${error.response?.data?.message || 'Error'}`);
                }
            },
            allowOutsideClick: () => !Swal.isLoading()
        });
    };





    useEffect(() => {
        if (activeTab === 'invoices') fetchInvoices();
    }, [activeTab, invoiceFilters.search, selectedDate]); // Re-fetch when search or main calendar date changes

    const confirmPause = async () => {
        const result = await Swal.fire({
            title: 'Confirm Pause & Notify Patients?',
            text: `This will stop the queue and EMAIL all upcoming patients about the ${queueStatus?.pauseDuration} min delay.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, Stop Queue & Notify'
        });

        if (result.isConfirmed) {
            try {
                await api.post('/queue/confirm', { doctorId: selectedDoctor });
                Swal.fire('Paused', 'Queue stopped and notifications sent.', 'success');
                fetchQueueStatus();
            } catch (error) {
                Swal.fire('Error', 'Failed to confirm pause', 'error');
            }
        }
    };

    const resumeQueue = async () => {
        try {
            await api.post('/queue/resume', { doctorId: selectedDoctor });
            Swal.fire('Resumed', 'Queue is active again.', 'success');
            fetchQueueStatus();
        } catch (error) {
            Swal.fire('Error', 'Failed to resume', 'error');
        }
    };

    // Emergency Logic
    const toggleEmergency = async () => {
        try {
            if (emergencyMode) {
                // Resolve Emergency
                await api.post('/emergency/resolve', {});
                setEmergencyMode(false);
                Swal.fire('Info', 'Emergency mode deactivated. Queue resuming.', 'info');
            } else {
                // Trigger Emergency
                const confirm = await Swal.fire({
                    title: 'Trigger Emergency?',
                    text: "This will create a high-priority 'Emergency Patient' at the top of the queue and pause normal operations.",
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#d33',
                    confirmButtonText: 'Yes, Trigger Emergency'
                });

                if (confirm.isConfirmed) {
                    await api.post('/emergency/trigger', { doctorId: selectedDoctor });
                    setEmergencyMode(true);
                    Swal.fire('Emergency Triggered', 'Emergency patient added to top of queue.', 'success');
                }
            }
            fetchQueueStatus();
            fetchAppointments();
        } catch (error) {
            console.error(error);
            Swal.fire('Error', 'Failed to toggle emergency mode', 'error');
        }
    };

    // MR Checking
    const handleMRCheckin = async (e) => {
        e.preventDefault();
        try {
            await api.post('/mr-visits/checkin', { ...mrData });
            Swal.fire('Success', 'MR Check-in Logged', 'success');
            setShowMRModal(false);
            setMrData({ mrName: '', email: '', companyName: '', doctorId: '', purpose: '' });
            if (activeTab === 'mr_visits') fetchMRVisits();

        } catch (error) {
            Swal.fire('Error', 'Check-in failed', 'error');
        }
    };

    // VITALS LOGIC
    const handleOpenVitals = async (appt) => {
        try {
            // Fetch existing vitals if any
            const res = await api.get(`/vitals/appointment/${appt._id}`);

            if (res.data) {
                const existing = res.data;
                setVitalsData({
                    patientId: appt.patientId?._id || (typeof appt.patientId === 'string' ? appt.patientId : null),
                    appointmentId: appt._id,
                    bp: existing.vitals?.bp || '',
                    temperature: existing.vitals?.temperature || '',
                    pulse: existing.vitals?.pulse || '',
                    weight: existing.vitals?.weight || '',
                    spo2: existing.vitals?.spo2 || '',
                    notes: existing.notes || '',
                    isUpdate: true
                });
            } else {
                setVitalsData({
                    patientId: appt.patientId?._id || (typeof appt.patientId === 'string' ? appt.patientId : null),
                    appointmentId: appt._id,
                    bp: '', temperature: '', pulse: '', weight: '', spo2: '', notes: '',
                    isUpdate: false
                });
            }
            setShowVitalsModal(true);
        } catch (error) {
            console.error("Fetch Vitals Error:", error);
            // Even if fetch fails, let them open the modal with empty fields
            setVitalsData({
                patientId: appt.patientId?._id || (typeof appt.patientId === 'string' ? appt.patientId : null),
                appointmentId: appt._id,
                bp: '', temperature: '', pulse: '', weight: '', spo2: '', notes: ''
            });
            setShowVitalsModal(true);
        }
    };

    const handleSaveVitals = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('token');
            // Nest vitals correctly for backend schema
            const payload = {
                patientId: vitalsData.patientId || null,
                appointmentId: vitalsData.appointmentId || null,
                vitals: {
                    bp: vitalsData.bp,
                    temperature: vitalsData.temperature,
                    pulse: vitalsData.pulse,
                    weight: vitalsData.weight,
                    spo2: vitalsData.spo2
                },
                notes: vitalsData.notes,
                symptoms: [], // Add symptoms input if needed
                clinicId: user.clinicId
            };

            await api.post('/vitals', payload);

            Swal.fire('Success', 'Vitals recorded successfully', 'success');
            setShowVitalsModal(false);
        } catch (error) {
            console.error("Save Vitals Error:", error);
            Swal.fire('Error', 'Failed to save vitals', 'error');
        }
    };

    // Profile Logic
    useEffect(() => {
        if (user) {
            setProfileData({
                name: user.name || '',
                phone: user.phone || ''
            });
        }
    }, [user]);

    const handleProfileUpdate = async (e) => {
        e.preventDefault();
        if (passwords.new && passwords.new !== passwords.confirm) {
            return Swal.fire('Error', 'Passwords do not match', 'error');
        }

        try {
            const token = localStorage.getItem('token');
            const dataToUpdate = {
                name: profileData.name,
                phone: profileData.phone
            };
            if (passwords.new) dataToUpdate.password = passwords.new;

            await api.put('/users/profile', dataToUpdate);

            Swal.fire('Success', 'Profile updated successfully', 'success');
            setPasswords({ new: '', confirm: '' });
        } catch (error) {
            Swal.fire('Error', error.response?.data?.message || 'Update failed', 'error');
        }
    };

    const handleViewDetails = async (appt) => {
        setDetailsData({ appointment: appt, analysis: null, prescription: null, loading: true });
        setShowDetailsModal(true);
        try {
            // Parallel Fetch
            const [analysisRes, prescriptionRes] = await Promise.all([
                api.get(`/assistant/analysis/${appt._id}`).catch(e => ({ data: null })),
                api.get(`/prescriptions/${appt._id}`).catch(e => ({ data: null }))
            ]);

            setDetailsData({
                appointment: appt,
                analysis: analysisRes.data,
                prescription: prescriptionRes.data,
                loading: false
            });

        } catch (error) {
            console.error("Fetch Details Failed:", error);
            setDetailsData(prev => ({ ...prev, loading: false }));
        }
    };

    return (
        <section className={mobileMenu ? "overlay active" : "overlay"}>
            <Sidebar isSidebarActive={sidebarActive} mobileMenu={mobileMenu} closeSidebar={closeSidebar} />
            <main className={sidebarActive ? "dashboard-main active" : "dashboard-main"}>
                <Header toggleSidebar={toggleSidebar} mobileMenuControl={mobileMenuControl} />

                <div className="dashboard-main-body">
                    {/* Top Stats / Controls */}
                    <div className="d-flex flex-wrap align-items-center justify-content-between mb-24 gap-3">
                        <div>
                            <h6 className="fw-semibold mb-1">Reception / Assistant Board</h6>
                            <p className="text-secondary-light mb-0">Manage Appointments & Live Queue</p>
                        </div>
                        <div className="d-flex gap-3">
                            <button onClick={() => setShowMRModal(true)} className="btn btn-outline-primary d-flex align-items-center gap-2">
                                <Icon icon="solar:users-group-two-rounded-bold" /> MR Visit
                            </button>
                            <button onClick={toggleEmergency} className={`btn ${emergencyMode ? 'btn-danger' : 'btn-warning'} d-flex align-items-center gap-2`}>
                                <Icon icon="solar:danger-triangle-bold" /> {emergencyMode ? 'STOP Emergency' : 'Trigger Emergency'}
                            </button>
                            <button onClick={() => setShowBooking(true)} className="btn btn-primary d-flex align-items-center gap-2">
                                <Icon icon="solar:calendar-add-bold" /> New Appointment
                            </button>
                        </div>
                    </div>

                    {/* QUEUE TAB */}
                    {activeTab === 'queue' && (
                        emergencyMode && (
                            <div className="alert alert-danger d-flex align-items-center gap-2 mb-4">
                                <Icon icon="solar:danger-circle-bold" className="text-xl" />
                                <strong>EMERGENCY MODE ACTIVE:</strong> Queue is currently paused. Please attend to the emergency.
                            </div>
                        )
                    )}

                    <Row className="gy-4">
                        {/* Left Column: Calendar & Filters */}
                        <Col lg={4}>
                            <Card className="h-100 border-0 shadow-sm radius-12">
                                <Card.Body>
                                    <h6 className="mb-3">Select Date & Doctor</h6>
                                    <Form.Select
                                        value={selectedDoctor}
                                        onChange={(e) => setSelectedDoctor(e.target.value)}
                                        className="mb-4 form-select-lg fw-medium bg-light"
                                    >
                                        <option value="" disabled>Select Doctor...</option>
                                        {doctors.map(d => (
                                            <option key={d._id} value={d._id}>{d.name} ({d.specialization})</option>
                                        ))}
                                    </Form.Select>
                                    {doctors.length === 0 && !loading && (
                                        <div className="alert alert-warning py-3 d-flex align-items-center gap-2 mb-4 text-xs">
                                            <Icon icon="solar:danger-triangle-bold" className="text-xl flex-shrink-0" />
                                            <div>No doctors found linked to your clinic. Please contact Admin.</div>
                                        </div>
                                    )}

                                    {/* QUEUE ALERTS */}
                                    {queueStatus?.status === 'PauseRequested' && (
                                        <div className="alert alert-warning shadow-sm border-warning mb-4">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div className="d-flex align-items-center gap-3">
                                                    <div className="bg-warning-subtle text-warning-emphasis p-2 rounded-circle">
                                                        <Icon icon="solar:bell-bing-bold" className="text-2xl" />
                                                    </div>
                                                    <div>
                                                        <h6 className="mb-1 fw-bold text-warning-emphasis">Doctor Requests Queue Pause</h6>
                                                        <p className="mb-0 text-secondary-emphasis">
                                                            Reason: <strong>{queueStatus.reason}</strong> | Duration: <strong>{queueStatus.pauseDuration} mins</strong>
                                                        </p>
                                                    </div>
                                                </div>
                                                <Button variant="danger" onClick={confirmPause}>Stop Queue & Notify Patients</Button>
                                            </div>
                                        </div>
                                    )}

                                    {queueStatus?.status === 'Paused' && (
                                        <div className="alert alert-danger shadow-sm border-danger mb-4">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div className="d-flex align-items-center gap-3">
                                                    <div className="bg-danger-subtle text-danger-emphasis p-2 rounded-circle">
                                                        <Icon icon="solar:forbidden-circle-bold" className="text-2xl" />
                                                    </div>
                                                    <div>
                                                        <h6 className="mb-1 fw-bold text-danger-emphasis">Queue is PAUSED</h6>
                                                        <p className="mb-0 text-secondary-emphasis">
                                                            Resuming at: <strong>{new Date(queueStatus.resumeTime).toLocaleTimeString()}</strong>
                                                        </p>
                                                    </div>
                                                </div>
                                                <Button variant="outline-success" onClick={resumeQueue}>Resume Now</Button>
                                            </div>
                                        </div>
                                    )}

                                    <div className="calendar-wrapper p-2 bg-light rounded-3">
                                        <Calendar
                                            onChange={setSelectedDate}
                                            value={selectedDate}
                                            className="w-100 border-0 bg-transparent"
                                            prevLabel={<Icon icon="solar:alt-arrow-left-bold" width="20" />}
                                            nextLabel={<Icon icon="solar:alt-arrow-right-bold" width="20" />}
                                            prev2Label={null}
                                            next2Label={null}
                                        />
                                    </div>

                                    <div className="mt-4">
                                        <h6 className="mb-2">Queued Today</h6>
                                        <div className="d-flex align-items-center justify-content-between p-3 bg-primary-50 rounded-3 border border-primary-100">
                                            <div>
                                                <small className="text-primary-600 fw-medium">Total Appointments</small>
                                                <h4 className="mb-0 text-primary-600">{appointments.length}</h4>
                                            </div>
                                            <Icon icon="solar:clipboard-list-bold-duotone" className="text-4xl text-primary-300" />
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* Right Column: View Content */}
                        <Col lg={8}>
                            <Card className="h-100 border-0 shadow-sm radius-12 overflow-hidden d-flex flex-column">
                                <Card.Header className="bg-white border-bottom py-3 px-4 d-flex justify-content-between align-items-center flex-shrink-0">
                                    <h5 className="mb-0 fw-bold text-dark">
                                        {activeTab === 'queue' && 'Live Patient Queue'}
                                        {activeTab === 'appointments' && 'All Appointments'}
                                        {activeTab === 'mr_visits' && 'Medical Rep. Visits'}
                                        {activeTab === 'invoices' && 'Clinic Invoices'}
                                        {activeTab === 'settings' && 'Account Settings'}
                                    </h5>
                                    {activeTab === 'queue' && <Badge bg="success" className="px-3 py-2 rounded-pill">Today</Badge>}
                                    {activeTab === 'invoices' && (
                                        <div className="d-flex align-items-center gap-2 d-print-none">
                                            <Form.Control
                                                type="text"
                                                placeholder="Search Patient/Invoice..."
                                                value={invoiceFilters.search}
                                                onChange={(e) => setInvoiceFilters({ ...invoiceFilters, search: e.target.value })}
                                                className="form-control-sm w-auto shadow-sm border-0 bg-light"
                                                style={{ minWidth: '200px' }}
                                            />
                                            <Button
                                                variant="light"
                                                size="sm"
                                                className="border shadow-sm d-flex align-items-center gap-2"
                                                onClick={() => { setInvoiceFilters({ ...invoiceFilters, search: '' }); fetchInvoices(); }}
                                            >
                                                <Icon icon="solar:restart-bold" />
                                            </Button>
                                        </div>
                                    )}
                                </Card.Header>
                                <Card.Body className="p-0 flex-grow-1 overflow-hidden">
                                    {/* LIVE QUEUE VIEW */}
                                    {activeTab === 'queue' && (
                                        <div className="h-100 overflow-auto">
                                            {loading ? <div className="p-5 text-center text-muted">Loading queue...</div> :
                                                queue.length === 0 ? <div className="p-5 text-center text-secondary">
                                                    <Icon icon="solar:sofa-bold-duotone" className="text-4xl mb-3 op-50" />
                                                    <p>No active patients in queue for today.</p>
                                                </div> :
                                                    <div className="table-responsive">
                                                        <table className="table table-hover align-middle mb-0">
                                                            <thead className="bg-light sticky-top">
                                                                <tr>
                                                                    <th className="ps-4">Q-ID</th>
                                                                    <th>Patient</th>
                                                                    <th>Time</th>
                                                                    <th>Status</th>
                                                                    <th className="text-end pe-4">Actions</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {queue.map((appt) => (
                                                                    <tr key={appt._id} className={`${appt.isEmergency ? 'bg-danger-subtle border-start border-4 border-danger' : appt.status === 'Active' ? 'bg-primary-50 border-start border-4 border-primary' : ''}`}>
                                                                        <td className={`ps-4 fw-bold ${appt.isEmergency ? 'text-danger' : 'text-primary'}`}>
                                                                            {appt.isEmergency ? <Icon icon="solar:danger-triangle-bold" className="me-1" /> : '#'}
                                                                            {appt.queueNumber}
                                                                        </td>
                                                                        <td>
                                                                            <div className={`fw-medium ${appt.isEmergency ? 'text-danger' : 'text-dark'}`}>{appt.patientId?.name || appt.guestName}</div>
                                                                            <div className="text-xs text-secondary">{appt.patientId?.phone || appt.guestPhone}</div>
                                                                        </td>
                                                                        <td>{appt.timeSlot}</td>
                                                                        <td>
                                                                            <Badge bg={appt.status === 'Active' ? 'success' : appt.isEmergency ? 'danger' : 'info'} className="px-2 py-1">
                                                                                {appt.status === 'Active' ? 'NOW SERVING' : appt.status}
                                                                            </Badge>
                                                                        </td>
                                                                        <td className="text-end pe-4">
                                                                            <div className="d-flex align-items-center justify-content-end gap-2">
                                                                                {/* Call Next (Upcoming Only) */}
                                                                                {appt.status === 'Upcoming' && (
                                                                                    <OverlayTrigger overlay={<Tooltip>Call Next</Tooltip>}>
                                                                                        <Button
                                                                                            variant="success"
                                                                                            size="sm"
                                                                                            className="rounded-circle p-2 d-flex align-items-center justify-content-center"
                                                                                            disabled={queueStatus?.status === 'Paused'}
                                                                                            onClick={() => handleStartVisit(appt)}
                                                                                        >
                                                                                            <Icon icon="solar:play-circle-bold-duotone" className="text-lg" />
                                                                                        </Button>
                                                                                    </OverlayTrigger>
                                                                                )}

                                                                                {/* Manage Visit (Active Only) */}
                                                                                {appt.status === 'Active' && (
                                                                                    <OverlayTrigger overlay={<Tooltip>Manage Visit</Tooltip>}>
                                                                                        <Button
                                                                                            variant="primary"
                                                                                            size="sm"
                                                                                            className="rounded-circle p-2 d-flex align-items-center justify-content-center"
                                                                                            onClick={() => handleOpenVisitModal(appt)}
                                                                                        >
                                                                                            <Icon icon="solar:stethoscope-bold-duotone" className="text-lg" />
                                                                                        </Button>
                                                                                    </OverlayTrigger>
                                                                                )}

                                                                                {/* Vitals (Always) */}
                                                                                <OverlayTrigger overlay={<Tooltip>Record Vitals</Tooltip>}>
                                                                                    <Button
                                                                                        variant="info"
                                                                                        size="sm"
                                                                                        className="rounded-circle p-2 d-flex align-items-center justify-content-center text-white"
                                                                                        onClick={() => handleOpenVitals(appt)}
                                                                                    >
                                                                                        <Icon icon="solar:heart-pulse-bold-duotone" className="text-lg" />
                                                                                    </Button>
                                                                                </OverlayTrigger>

                                                                                {/* Reschedule (If Conflict) */}
                                                                                {checkConflict(appt) && (
                                                                                    <OverlayTrigger overlay={<Tooltip>Reschedule Conflict</Tooltip>}>
                                                                                        <Button
                                                                                            variant="warning"
                                                                                            size="sm"
                                                                                            className="rounded-circle p-2 d-flex align-items-center justify-content-center text-white"
                                                                                            onClick={() => handleReschedule(appt)}
                                                                                        >
                                                                                            <Icon icon="solar:calendar-date-bold-duotone" className="text-lg" />
                                                                                        </Button>
                                                                                    </OverlayTrigger>
                                                                                )}

                                                                                {/* Skip / Missed (Upcoming/Active) */}
                                                                                {(appt.status === 'Upcoming' || appt.status === 'Active') && (
                                                                                    <OverlayTrigger overlay={<Tooltip>Mark Missed</Tooltip>}>
                                                                                        <Button
                                                                                            variant="danger"
                                                                                            size="sm"
                                                                                            className="rounded-circle p-2 d-flex align-items-center justify-content-center"
                                                                                            onClick={() => updateStatus(appt._id, 'Missed')}
                                                                                        >
                                                                                            <Icon icon="solar:close-circle-bold-duotone" className="text-lg" />
                                                                                        </Button>
                                                                                    </OverlayTrigger>
                                                                                )}
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                            }
                                        </div>
                                    )}

                                    {/* APPOINTMENTS VIEW */}
                                    {activeTab === 'appointments' && (
                                        <div className="h-100 overflow-auto">
                                            {loading ? <div className="p-5 text-center">Loading appointments...</div> :
                                                <div className="table-responsive">
                                                    <table className="table table-hover align-middle mb-0">
                                                        <thead className="bg-light sticky-top">
                                                            <tr>
                                                                <th className="ps-4">Seq</th>
                                                                <th>Patient</th>
                                                                <th>Type</th>
                                                                <th>Time</th>
                                                                <th>Status</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {appointments.length === 0 ? <tr><td colSpan="5" className="text-center p-4">No appointments found.</td></tr> :
                                                                appointments.map((appt) => (
                                                                    <tr key={appt._id}>
                                                                        <td className="ps-4 text-secondary">#{appt.queueNumber}</td>
                                                                        <td>
                                                                            <div className="fw-medium">{appt.patientId?.name || appt.guestName}</div>
                                                                            <div className="text-xs text-secondary">{appt.patientId?.phone || appt.guestPhone}</div>
                                                                        </td>
                                                                        <td>
                                                                            {appt.type === 'Online' ? <Badge bg="primary" text="white">Online</Badge> : <Badge bg="secondary" text="white">Physical</Badge>}
                                                                        </td>
                                                                        <td>{appt.timeSlot}</td>
                                                                        <td>
                                                                            {appt.status === 'Completed' || appt.status === 'Missed' ? (
                                                                                <>
                                                                                    <span className={`badge bg-${appt.status === 'Completed' ? 'success' : 'danger'}-subtle text-${appt.status === 'Completed' ? 'success' : 'danger'}`}>
                                                                                        {appt.status}
                                                                                    </span>
                                                                                    <Button
                                                                                        variant="link"
                                                                                        size="sm"
                                                                                        className="p-0 ms-2 text-primary"
                                                                                        onClick={() => handleViewDetails(appt)}
                                                                                        title="View Full Details"
                                                                                    >
                                                                                        <Icon icon="solar:eye-bold" className="text-lg" />
                                                                                    </Button>
                                                                                </>
                                                                            ) : appt.status === 'Cancelled' ? (
                                                                                <div className="d-flex align-items-center gap-2">
                                                                                    <Badge bg="danger" className="px-2 py-1">Cancelled</Badge>
                                                                                    <Button
                                                                                        variant="link"
                                                                                        size="sm"
                                                                                        className="p-0 ms-2 text-primary"
                                                                                        onClick={() => handleViewCancellation(appt)}
                                                                                        title="View Cancellation Details"
                                                                                    >
                                                                                        <Icon icon="solar:eye-bold" className="text-lg" />
                                                                                    </Button>
                                                                                </div>
                                                                            ) : (
                                                                                <span className={`badge px-3 py-2 rounded-3 ${appt.status === 'Upcoming' ? 'bg-warning-subtle text-warning' :
                                                                                    appt.status === 'Active' ? 'bg-primary-subtle text-primary' :
                                                                                        'bg-secondary-subtle text-secondary'
                                                                                    }`}>
                                                                                    {appt.status}
                                                                                </span>
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            }
                                        </div>
                                    )}

                                    {/* MR VISITS VIEW */}
                                    {activeTab === 'mr_visits' && (
                                        <div className="h-100 d-flex flex-column">
                                            <Tab.Container defaultActiveKey="upcoming">
                                                <div className="px-4 pt-3 border-bottom bg-light-subtle">
                                                    <Nav variant="tabs" className="border-bottom-0">
                                                        <Nav.Item>
                                                            <Nav.Link eventKey="upcoming" className="fw-medium px-4">Upcoming</Nav.Link>
                                                        </Nav.Item>
                                                        <Nav.Item>
                                                            <Nav.Link eventKey="completed" className="fw-medium px-4">Completed</Nav.Link>
                                                        </Nav.Item>
                                                    </Nav>
                                                </div>
                                                <div className="flex-grow-1 overflow-auto bg-white">
                                                    <Tab.Content className="h-100">
                                                        <Tab.Pane eventKey="upcoming" className="h-100">
                                                            {(() => {
                                                                // Filter Upcoming (Not Completed/Resolved)
                                                                const upcoming = mrVisits.filter(v => v.status !== 'Completed' && v.status !== 'Resolved');
                                                                if (loading) return <div className="p-5 text-center">Loading MR visits...</div>;
                                                                if (upcoming.length === 0) return (
                                                                    <div className="p-5 text-center text-secondary">
                                                                        <Icon icon="solar:user-id-bold-duotone" className="text-4xl mb-3 opacity-50" />
                                                                        <p className="fw-medium">No upcoming MR visits.</p>
                                                                    </div>
                                                                );
                                                                return (
                                                                    <div className="table-responsive">
                                                                        <table className="table table-hover align-middle mb-0">
                                                                            <thead className="bg-light sticky-top">
                                                                                <tr>
                                                                                    <th className="ps-4">Time</th>
                                                                                    <th>MR Name</th>
                                                                                    <th>Email</th>
                                                                                    <th>Company</th>
                                                                                    <th>Status</th>
                                                                                    <th>Purpose</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {upcoming.map((visit) => (
                                                                                    <tr key={visit._id}>
                                                                                        <td className="ps-4 text-secondary">
                                                                                            {new Date(visit.checkInTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                                                        </td>
                                                                                        <td className="fw-medium">{visit.mrName}</td>
                                                                                        <td className="text-secondary small">{visit.email || '-'}</td>
                                                                                        <td>{visit.companyName}</td>
                                                                                        <td>
                                                                                            <Badge bg="warning" className="rounded-pill px-3">
                                                                                                {visit.status || 'Waiting'}
                                                                                            </Badge>
                                                                                        </td>
                                                                                        <td className="text-truncate" style={{ maxWidth: '200px' }}>{visit.purpose || 'Not Mentioned'}</td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                );
                                                            })()}
                                                        </Tab.Pane>
                                                        <Tab.Pane eventKey="completed" className="h-100">
                                                            {(() => {
                                                                // Filter Completed/Resolved
                                                                const completed = mrVisits.filter(v => v.status === 'Completed' || v.status === 'Resolved');
                                                                if (loading) return <div className="p-5 text-center">Loading...</div>;
                                                                if (completed.length === 0) return (
                                                                    <div className="p-5 text-center text-secondary">
                                                                        <Icon icon="solar:check-circle-bold-duotone" className="text-4xl mb-3 opacity-50" />
                                                                        <p className="fw-medium">No completed MR visits.</p>
                                                                    </div>
                                                                );
                                                                return (
                                                                    <div className="table-responsive">
                                                                        <table className="table table-hover align-middle mb-0">
                                                                            <thead className="bg-light sticky-top">
                                                                                <tr>
                                                                                    <th className="ps-4">Time</th>
                                                                                    <th>MR Name</th>
                                                                                    <th>Email</th>
                                                                                    <th>Company</th>
                                                                                    <th>Status</th>
                                                                                    <th>Purpose</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {completed.map((visit) => (
                                                                                    <tr key={visit._id}>
                                                                                        <td className="ps-4 text-secondary">
                                                                                            {new Date(visit.checkInTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                                                        </td>
                                                                                        <td className="fw-medium">{visit.mrName}</td>
                                                                                        <td className="text-secondary small">{visit.email || '-'}</td>
                                                                                        <td>{visit.companyName}</td>
                                                                                        <td>
                                                                                            <Badge bg="success" className="rounded-pill px-3">Completed</Badge>
                                                                                        </td>
                                                                                        <td className="text-truncate" style={{ maxWidth: '200px' }}>{visit.purpose || 'Not Mentioned'}</td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                );
                                                            })()}
                                                        </Tab.Pane>
                                                    </Tab.Content>
                                                </div>
                                            </Tab.Container>
                                        </div>
                                    )}

                                    {/* INVOICES VIEW */}
                                    {activeTab === 'invoices' && (
                                        <div className="h-100 d-flex flex-column">
                                            <div className="flex-grow-1 overflow-auto">
                                                <div className="table-responsive">
                                                    <table className="table table-hover align-middle mb-0">
                                                        <thead className="bg-light sticky-top">
                                                            <tr>
                                                                <th className="ps-4 text-xs font-bold text-uppercase text-secondary">Invoice Info</th>
                                                                <th className="text-xs font-bold text-uppercase text-secondary">Patient</th>
                                                                <th className="text-xs font-bold text-uppercase text-secondary">Date</th>
                                                                <th className="text-xs font-bold text-uppercase text-secondary">Amount</th>
                                                                <th className="text-xs font-bold text-uppercase text-secondary">Status</th>
                                                                <th className="text-center pe-4 text-xs font-bold text-uppercase text-secondary">Action</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {loading ? (
                                                                <tr><td colSpan="6" className="text-center py-5"><Spinner animation="border" variant="primary" size="sm" /></td></tr>
                                                            ) : (() => {
                                                                const filtered = invoices.filter(inv => {
                                                                    const searchLower = invoiceFilters.search.toLowerCase();
                                                                    const matchesSearch = !searchLower ||
                                                                        inv.invoiceNumber?.toLowerCase().includes(searchLower) ||
                                                                        inv.patientId?.name?.toLowerCase().includes(searchLower) ||
                                                                        inv.customerInfo?.name?.toLowerCase().includes(searchLower);

                                                                    const matchesDate = !invoiceFilters.date ||
                                                                        new Date(inv.createdAt).toISOString().split('T')[0] === invoiceFilters.date;

                                                                    return matchesSearch && matchesDate;
                                                                });

                                                                if (filtered.length === 0) {
                                                                    return (
                                                                        <tr>
                                                                            <td colSpan="6" className="text-center py-5 text-secondary opacity-50">
                                                                                <Icon icon="solar:bill-list-bold-duotone" width="48" height="48" className="mb-2" />
                                                                                <p className="mb-0">No invoices found matching your criteria.</p>
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                }

                                                                return filtered.map((inv) => (
                                                                    <tr key={inv._id}>
                                                                        <td className="ps-4">
                                                                            <div className="d-flex align-items-center gap-2">
                                                                                <div className="w-32-px h-32-px rounded-circle bg-primary-50 text-primary-600 d-flex justify-content-center align-items-center">
                                                                                    <Icon icon="solar:document-text-bold" />
                                                                                </div>
                                                                                <div>
                                                                                    <span className="d-block text-sm fw-bold text-dark">{inv.invoiceNumber}</span>
                                                                                    <span className="d-block text-xs text-secondary">#{inv._id.slice(-6).toUpperCase()}</span>
                                                                                </div>
                                                                            </div>
                                                                        </td>
                                                                        <td>
                                                                            <div className="d-flex flex-column">
                                                                                <span className="text-sm fw-semibold text-dark">{inv.patientId?.name || inv.customerInfo?.name || 'Guest'}</span>
                                                                                <span className="text-xs text-secondary">{inv.patientId?.phone || inv.customerInfo?.phone || 'N/A'}</span>
                                                                            </div>
                                                                        </td>
                                                                        <td>
                                                                            <span className="text-sm text-secondary">{new Date(inv.issueDate || inv.createdAt).toLocaleDateString()}</span>
                                                                        </td>
                                                                        <td>
                                                                            <span className="text-sm fw-bold text-dark">
                                                                                {inv.currency} {(inv.amount).toLocaleString()}
                                                                            </span>
                                                                        </td>
                                                                        <td>
                                                                            <Badge bg={inv.status === 'Paid' ? 'success' : inv.status === 'Pending' ? 'warning' : 'danger'} className="px-2 py-1 rounded-pill fw-medium text-xs">
                                                                                {inv.status || 'Pending'}
                                                                            </Badge>
                                                                        </td>
                                                                        <td className="pe-4 text-center">
                                                                            <Dropdown align="end">
                                                                                <Dropdown.Toggle as={CustomToggle} />
                                                                                <Dropdown.Menu className="border-0 shadow-lg rounded-3 p-2">
                                                                                    <Dropdown.Item onClick={() => { setSelectedInvoice(inv); setShowInvoiceModal(true); }} className="rounded-2 mb-1">
                                                                                        <Icon icon="solar:eye-bold" className="me-2 text-primary" /> View / Print
                                                                                    </Dropdown.Item>
                                                                                    <Dropdown.Item onClick={() => handleEmailInvoice(inv._id)} className="rounded-2">
                                                                                        <Icon icon="solar:letter-bold" className="me-2 text-success" /> Email Receipt
                                                                                    </Dropdown.Item>
                                                                                </Dropdown.Menu>
                                                                            </Dropdown>
                                                                        </td>
                                                                    </tr>
                                                                ));
                                                            })()}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    )}



                                    {/* SETTINGS VIEW */}
                                    {activeTab === 'settings' && (
                                        <div className="p-4 h-100 overflow-auto">
                                            <Form onSubmit={handleProfileUpdate}>
                                                <Row>
                                                    <Col md={6} className="mb-3">
                                                        <Form.Label>Full Name</Form.Label>
                                                        <Form.Control type="text" value={profileData.name} onChange={(e) => setProfileData({ ...profileData, name: e.target.value })} />
                                                    </Col>
                                                    <Col md={6} className="mb-3">
                                                        <Form.Label>Email</Form.Label>
                                                        <Form.Control type="email" value={user?.email} readOnly className="bg-light" />
                                                    </Col>
                                                    <Col md={6} className="mb-3">
                                                        <Form.Label>Role</Form.Label>
                                                        <Form.Control type="text" value={user?.role} readOnly className="bg-light" />
                                                    </Col>
                                                    <Col md={6} className="mb-3">
                                                        <Form.Label>Phone</Form.Label>
                                                        <Form.Control type="text" value={profileData.phone} onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })} placeholder="Update Phone..." />
                                                    </Col>
                                                    <Col md={12}>
                                                        <hr className="my-3" />
                                                        <h6 className="text-danger">Change Password</h6>
                                                    </Col>
                                                    <Col md={6} className="mb-3">
                                                        <Form.Label>New Password</Form.Label>
                                                        <Form.Control type="password" value={passwords.new} onChange={(e) => setPasswords({ ...passwords, new: e.target.value })} placeholder="New Password" />
                                                    </Col>
                                                    <Col md={6} className="mb-3">
                                                        <Form.Label>Confirm Password</Form.Label>
                                                        <Form.Control type="password" value={passwords.confirm} onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })} placeholder="Confirm New Password" />
                                                    </Col>
                                                </Row>
                                                <div className="text-end mt-3">
                                                    <Button type="submit" variant="primary">Update Profile</Button>
                                                </div>
                                            </Form>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* BOOKING MODAL */}
                    < Modal show={showBooking} onHide={() => setShowBooking(false)} centered >
                        <Modal.Header closeButton>
                            <Modal.Title>Book New Appointment</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Form onSubmit={handleBooking}>
                                {/* Booking Mode Selection */}
                                <div className="d-flex align-items-center gap-3">
                                    <div className="d-flex align-items-center gap-2">
                                        <b className="mb-0">Booking Mode:</b>
                                        <Form.Check
                                            type="radio"
                                            id="mode-manual"
                                            label="Walk-in / Manual"
                                            name="bookingMode"
                                            value="Manual"
                                            checked={true}
                                            readOnly
                                            className="cursor-pointer"
                                        />
                                    </div>
                                </div>

                                <Form.Group className="mb-3">
                                    <Form.Label>Patient Full Name (Walk-in)</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={bookingData.patientName}
                                        onChange={e => setBookingData({ ...bookingData, patientName: e.target.value })}
                                        placeholder="e.g. Amit Kumar"
                                        required
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Phone Number</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={bookingData.patientPhone}
                                        onChange={e => setBookingData({ ...bookingData, patientPhone: e.target.value })}
                                        placeholder="Mobile Number"
                                        required
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Email Address <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="email"
                                        value={bookingData.patientEmail}
                                        onChange={e => setBookingData({ ...bookingData, patientEmail: e.target.value })}
                                        placeholder="email@example.com"
                                        required
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Select Doctor</Form.Label>
                                    <Form.Select
                                        value={bookingData.doctorId || selectedDoctor}
                                        onChange={e => setBookingData({ ...bookingData, doctorId: e.target.value })}
                                    >
                                        <option disabled value="">Choose...</option>
                                        {doctors.map(d => <option key={d._id} value={d._id}>{d.name}</option>)}
                                    </Form.Select>
                                </Form.Group>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Date</Form.Label>
                                            <Form.Control
                                                type="date"
                                                required
                                                min={new Date().toISOString().split('T')[0]}
                                                value={(() => {
                                                    try {
                                                        const d = bookingData.date instanceof Date ? bookingData.date : new Date(bookingData.date);
                                                        return !isNaN(d.getTime()) ? d.toISOString().split('T')[0] : '';
                                                    } catch (e) { return ''; }
                                                })()}
                                                onChange={e => setBookingData({ ...bookingData, date: new Date(e.target.value) })}
                                                disabled={bookingData.type === 'Emergency'}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Type</Form.Label>
                                            <Form.Select
                                                value={bookingData.type}
                                                onChange={e => {
                                                    const newType = e.target.value;
                                                    const updates = { type: newType };
                                                    if (newType === 'Emergency') updates.date = new Date();
                                                    setBookingData({ ...bookingData, ...updates });
                                                }}
                                            >
                                                <option value="Physical">Physical Visit</option>
                                                <option value="Online">Online Consult</option>
                                                <option value="Emergency">Emergency</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    <Col md={12}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Consultation Price (Reflected in Invoice/Link) <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="number"
                                                value={bookingData.price}
                                                onChange={e => setBookingData({ ...bookingData, price: e.target.value })}
                                                placeholder="e.g. 500"
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Form.Group className="mb-3">
                                    <Form.Label>Time Slot</Form.Label>
                                    <div className="d-flex flex-wrap gap-2">
                                        {(() => {
                                            const targetDocId = bookingData.doctorId || selectedDoctor;
                                            const doc = doctors.find(d => d._id === targetDocId);
                                            const targetDate = bookingData.date instanceof Date ? bookingData.date : new Date(bookingData.date);

                                            // Helper to generate slots
                                            const getSlots = () => {
                                                if (!doc?.availability || doc.availability.length === 0) return TIME_SLOTS;

                                                const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                                                const dayName = days[targetDate.getDay()]; // Dynamic date

                                                if (!dayName) return []; // Invalid date

                                                // Robust Match: "Monday" === "Monday" OR "Mon" === "Mon"
                                                const schedule = doc.availability.find(d =>
                                                    d.day === dayName ||
                                                    d.day === dayName.slice(0, 3) ||
                                                    d.day.toLowerCase() === dayName.toLowerCase()
                                                );

                                                if (!schedule || !schedule.isAvailable) return []; // Not available

                                                const slots = [];
                                                let [startH, startM] = schedule.startTime.split(':').map(Number);
                                                let [endH, endM] = schedule.endTime.split(':').map(Number);

                                                let current = new Date();
                                                current.setHours(startH, startM, 0, 0);
                                                const end = new Date();
                                                end.setHours(endH, endM, 0, 0);

                                                while (current < end) {
                                                    const hours = current.getHours().toString().padStart(2, '0');
                                                    const minutes = current.getMinutes().toString().padStart(2, '0');
                                                    const timeString = `${hours}:${minutes}`;

                                                    // Filter Past Times if Today
                                                    let isPast = false;
                                                    const now = new Date();
                                                    if (targetDate.toDateString() === now.toDateString()) {
                                                        const currentH = now.getHours();
                                                        const currentM = now.getMinutes();
                                                        const [slotH, slotM] = timeString.split(':').map(Number);
                                                        if (slotH < currentH || (slotH === currentH && slotM <= currentM)) {
                                                            isPast = true;
                                                        }
                                                    }

                                                    if (!isPast) slots.push(timeString);

                                                    current.setMinutes(current.getMinutes() + 30);
                                                }
                                                return slots;
                                            };

                                            const slots = getSlots();

                                            // Filter out slots that are already booked
                                            const availableSlots = slots.filter(t => {
                                                return !bookingAppointments.some(a => a.timeSlot === t && !['Missed', 'Cancelled'].includes(a.status));
                                            });

                                            if (availableSlots.length === 0) {
                                                return <div className="text-secondary small">No available slots for this day.</div>
                                            }

                                            return availableSlots.map(t => {
                                                return (
                                                    <Badge
                                                        key={t}
                                                        bg={bookingData.timeSlot === t ? 'primary' : 'light'}
                                                        text={bookingData.timeSlot === t ? 'white' : 'dark'}
                                                        className={`border p-2 fw-medium cursor-pointer ${bookingData.timeSlot === t ? 'shadow-sm border-primary' : ''}`}
                                                        onClick={() => setBookingData({ ...bookingData, timeSlot: t })}
                                                    >
                                                        {t}
                                                    </Badge>
                                                );
                                            });
                                        })()}
                                    </div>
                                    <input type="hidden" required value={bookingData.timeSlot} />
                                </Form.Group>
                                <div className="d-flex justify-content-end mt-4">
                                    <Button type="submit" variant="primary" disabled={!bookingData.timeSlot || !bookingData.patientPhone || !bookingData.price}>
                                        Confirm & Invoice
                                    </Button>
                                </div>
                            </Form>
                        </Modal.Body>
                    </Modal >

                    {/* MR VISIT MODAL */}
                    < Modal show={showMRModal} onHide={() => setShowMRModal(false)} centered >
                        <Modal.Header closeButton>
                            <Modal.Title>Medical Rep. Check-in</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Form onSubmit={handleMRCheckin}>
                                <Form.Group className="mb-3">
                                    <Form.Label>MR Name</Form.Label>
                                    <Form.Control type="text" required onChange={e => setMrData({ ...mrData, mrName: e.target.value })} />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Email Address</Form.Label>
                                    <Form.Control type="email" required onChange={e => setMrData({ ...mrData, email: e.target.value })} />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Company Name</Form.Label>
                                    <Form.Control type="text" required onChange={e => setMrData({ ...mrData, companyName: e.target.value })} />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Visiting Doctor</Form.Label>
                                    <Form.Select required onChange={e => setMrData({ ...mrData, doctorId: e.target.value })}>
                                        <option value="">Select Doctor...</option>
                                        {doctors.map(d => <option key={d._id} value={d._id}>{d.name}</option>)}
                                    </Form.Select>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Purpose (Optional)</Form.Label>
                                    <Form.Control as="textarea" rows={2} onChange={e => setMrData({ ...mrData, purpose: e.target.value })} />
                                </Form.Group>
                                <div className="d-flex justify-content-end">
                                    <Button type="submit" variant="primary">Log Visit</Button>
                                </div>
                            </Form>
                        </Modal.Body>
                    </Modal >

                    <VisitControlModal
                        show={showVisitModal}
                        onHide={() => setShowVisitModal(false)}
                        appointment={activeVisit}
                        onComplete={handleVisitComplete}
                    />

                    {/* VITALS RECORDING MODAL */}
                    <Modal show={showVitalsModal} onHide={() => setShowVitalsModal(false)} centered>
                        <Modal.Header closeButton>
                            <Modal.Title>{vitalsData.isUpdate ? 'Update Patient Vitals' : 'Record Patient Vitals'}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Form onSubmit={handleSaveVitals}>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Blood Pressure (BP)</Form.Label>
                                            <Form.Control type="text" placeholder="e.g. 120/80" value={vitalsData.bp} onChange={(e) => setVitalsData({ ...vitalsData, bp: e.target.value })} />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Temperature</Form.Label>
                                            <Form.Control type="text" placeholder="e.g. 98.6 F" value={vitalsData.temperature} onChange={(e) => setVitalsData({ ...vitalsData, temperature: e.target.value })} />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Pulse / Heart Rate</Form.Label>
                                            <Form.Control type="text" placeholder="e.g. 72" value={vitalsData.pulse} onChange={(e) => setVitalsData({ ...vitalsData, pulse: e.target.value })} />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Weight (kg)</Form.Label>
                                            <Form.Control type="text" placeholder="e.g. 70" value={vitalsData.weight} onChange={(e) => setVitalsData({ ...vitalsData, weight: e.target.value })} />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>SPO2 (%)</Form.Label>
                                            <Form.Control type="text" placeholder="e.g. 98" value={vitalsData.spo2} onChange={(e) => setVitalsData({ ...vitalsData, spo2: e.target.value })} />
                                        </Form.Group>
                                    </Col>
                                    <Col md={12}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Notes / Symptoms</Form.Label>
                                            <Form.Control as="textarea" rows={3} value={vitalsData.notes} onChange={(e) => setVitalsData({ ...vitalsData, notes: e.target.value })} />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <div className="d-flex justify-content-end gap-2 mt-3">
                                    <Button variant="light" onClick={() => setShowVitalsModal(false)}>Cancel</Button>
                                    <Button variant="primary" type="submit">
                                        {vitalsData.isUpdate ? 'Save Changes' : 'Record Vitals'}
                                    </Button>
                                </div>
                            </Form>
                        </Modal.Body>
                    </Modal>

                    {/* INVOICE DETAIL MODAL */}
                    {/* INVOICE DETAIL MODAL */}
                    {selectedInvoice && (
                        <PatientInvoiceDetailModal
                            show={showInvoiceModal}
                            onHide={() => setShowInvoiceModal(false)}
                            invoice={selectedInvoice}
                        />
                    )}

                    {/* APPOINTMENT DETAILS MODAL */}
                    <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg" centered>
                        <Modal.Header closeButton>
                            <Modal.Title className="fw-bold">Appointment Details</Modal.Title>
                        </Modal.Header>
                        <Modal.Body className="p-0">
                            {detailsData.loading ? (
                                <div className="p-5 text-center">
                                    <Spinner animation="border" variant="primary" />
                                    <p className="mt-2 text-secondary">Loading records...</p>
                                </div>
                            ) : !detailsData.appointment ? (
                                <div className="p-5 text-center text-danger">Data not found.</div>
                            ) : (
                                <div>
                                    {/* Header Info */}
                                    <div className="bg-light p-4 border-bottom">
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div>
                                                <h5 className="fw-bold mb-1">{detailsData.appointment.patientId?.name || detailsData.appointment.guestName}</h5>
                                                <div className="text-secondary text-sm mb-2">
                                                    {detailsData.appointment.patientId?.phone || detailsData.appointment.guestPhone}
                                                </div>
                                                <Badge bg={detailsData.appointment.status === 'Missed' ? 'danger' : 'success'}>
                                                    {detailsData.appointment.status}
                                                </Badge>
                                            </div>
                                            <div className="text-end">
                                                <div className="text-secondary text-xs">{new Date(detailsData.appointment.date).toLocaleDateString()}</div>
                                            </div>
                                        </div>

                                        {detailsData.appointment.status === 'Missed' && (
                                            <div className="mt-4 p-3 bg-danger-subtle border border-danger border-opacity-25 rounded-3">
                                                <div className="d-flex align-items-start gap-3">
                                                    <div className="bg-white p-2 rounded-circle shadow-sm text-danger d-flex align-items-center justify-content-center">
                                                        <Icon icon="solar:close-circle-bold-duotone" className="text-2xl" />
                                                    </div>
                                                    <div>
                                                        <h6 className="text-danger fw-bold mb-1">Appointment Missed</h6>
                                                        <p className="mb-0 text-danger-emphasis small opacity-75">Reason provided by Assistant:</p>
                                                        <p className="mb-0 fw-medium text-dark mt-1">"{detailsData.appointment.missedReason || 'Patient did not show up'}"</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Tabs */}
                                    <div className="p-3">
                                        <Tab.Container defaultActiveKey="clinical">
                                            <Nav variant="tabs" className="mb-3">
                                                <Nav.Item>
                                                    <Nav.Link eventKey="clinical" className="fw-medium">Clinical Data (Vitals & Notes)</Nav.Link>
                                                </Nav.Item>
                                                <Nav.Item>
                                                    <Nav.Link eventKey="prescription" className="fw-medium">Prescription</Nav.Link>
                                                </Nav.Item>
                                            </Nav>
                                            <Tab.Content>
                                                <Tab.Pane eventKey="clinical">
                                                    {/* VITALS GRID */}
                                                    <h6 className="text-primary fw-bold mb-3 d-flex align-items-center gap-2">
                                                        <Icon icon="solar:heart-pulse-bold" /> Recorded Vitals
                                                    </h6>
                                                    {detailsData.analysis?.vitals ? (
                                                        <div className="row g-3 mb-4">
                                                            <div className="col-6 col-md-3">
                                                                <div className="p-2 border rounded bg-light">
                                                                    <div className="text-xs text-secondary">BP</div>
                                                                    <div className="fw-bold">{detailsData.analysis.vitals.bp || '-'}</div>
                                                                </div>
                                                            </div>
                                                            <div className="col-6 col-md-3">
                                                                <div className="p-2 border rounded bg-light">
                                                                    <div className="text-xs text-secondary">Pulse</div>
                                                                    <div className="fw-bold">{detailsData.analysis.vitals.pulse || '-'} bpm</div>
                                                                </div>
                                                            </div>
                                                            <div className="col-6 col-md-3">
                                                                <div className="p-2 border rounded bg-light">
                                                                    <div className="text-xs text-secondary">Temp</div>
                                                                    <div className="fw-bold">{detailsData.analysis.vitals.temperature || '-'} F</div>
                                                                </div>
                                                            </div>
                                                            <div className="col-6 col-md-3">
                                                                <div className="p-2 border rounded bg-light">
                                                                    <div className="text-xs text-secondary">Weight</div>
                                                                    <div className="fw-bold">{detailsData.analysis.vitals.weight || '-'} kg</div>
                                                                </div>
                                                            </div>
                                                            <div className="col-6 col-md-3">
                                                                <div className="p-2 border rounded bg-light">
                                                                    <div className="text-xs text-secondary">SpO2</div>
                                                                    <div className="fw-bold">{detailsData.analysis.vitals.spo2 || '-'} %</div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <p className="text-secondary fst-italic mb-4">No vitals recorded.</p>
                                                    )}

                                                    <h6 className="text-primary fw-bold mb-2 d-flex align-items-center gap-2">
                                                        <Icon icon="solar:notebook-bold" /> Assistant Notes
                                                    </h6>
                                                    <div className="p-3 bg-light rounded border mb-3">
                                                        {detailsData.analysis?.notes || detailsData.analysis?.symptoms?.join(', ') || <span className="text-secondary">No notes added.</span>}
                                                    </div>

                                                    {detailsData.analysis?.reports && detailsData.analysis.reports.length > 0 && (
                                                        <>
                                                            <h6 className="text-primary fw-bold mb-2 d-flex align-items-center gap-2">
                                                                <Icon icon="solar:file-bold" /> Attached Reports
                                                            </h6>
                                                            <div className="d-flex flex-wrap gap-2">
                                                                {detailsData.analysis.reports.map((report, idx) => (
                                                                    <a key={idx} href={report} target="_blank" rel="noopener noreferrer" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2">
                                                                        <Icon icon="solar:document-bold" /> View Report {idx + 1}
                                                                    </a>
                                                                ))}
                                                            </div>
                                                        </>
                                                    )}
                                                </Tab.Pane>

                                                <Tab.Pane eventKey="prescription">
                                                    {detailsData.prescription ? (
                                                        <div>
                                                            <div className="table-responsive mb-3">
                                                                <table className="table table-sm table-bordered mb-0">
                                                                    <thead className="bg-light">
                                                                        <tr>
                                                                            <th>Medicine</th>
                                                                            <th>Valid For</th>
                                                                            <th>Frequency</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {detailsData.prescription.medicines?.map((med, idx) => (
                                                                            <tr key={idx}>
                                                                                <td className="fw-medium">{med.name}</td>
                                                                                <td>{med.dosage}</td>
                                                                                <td>{med.frequency}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                            {detailsData.prescription.notes && (
                                                                <div className="alert alert-info py-2">
                                                                    <strong>Doctor Instructions:</strong> {detailsData.prescription.notes}
                                                                </div>
                                                            )}

                                                            {detailsData.prescription.imageUrl && (
                                                                <div className="mt-3">
                                                                    <h6 className="fw-bold mb-2">Prescription Image</h6>
                                                                    <div className="border rounded p-2 bg-light text-center">
                                                                        <img
                                                                            src={detailsData.prescription.imageUrl}
                                                                            alt="Prescription"
                                                                            className="img-fluid rounded shadow-sm"
                                                                            style={{ maxHeight: '300px' }}
                                                                        />
                                                                        <div className="mt-2">
                                                                            <a href={detailsData.prescription.imageUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">
                                                                                <Icon icon="solar:maximize-square-minimalistic-bold" /> View Full Size
                                                                            </a>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <div className="text-center py-4 text-secondary">
                                                            <Icon icon="solar:document-add-linear" className="text-4xl mb-2 opacity-50" />
                                                            <p>No prescription issued.</p>
                                                        </div>
                                                    )}
                                                </Tab.Pane>
                                            </Tab.Content>
                                        </Tab.Container>
                                    </div>
                                    <div className="modal-footer bg-light border-top p-2">
                                        <Button variant="secondary" size="sm" onClick={() => setShowDetailsModal(false)}>Close</Button>
                                    </div>
                                </div>
                            )}
                        </Modal.Body>
                    </Modal>

                </div >
            </main >
            {/* CANCELLATION DETAILS MODAL */}
            <Modal show={showCancellationModal} onHide={() => setShowCancellationModal(false)} centered>
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold text-danger">Cancellation Details</Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2">
                    {cancellationData && (
                        <div className="text-center p-3">
                            <div className="w-60-px h-60-px bg-danger-50 rounded-circle flex-center mx-auto mb-3 text-danger">
                                <Icon icon="solar:close-circle-bold" className="text-4xl" />
                            </div>
                            <h5 className="fw-bold mb-3">{cancellationData.guestName || cancellationData.patientId?.name}</h5>

                            <div className="bg-light p-3 rounded-3 text-start mb-3">
                                <label className="text-xs text-secondary fw-bold text-uppercase mb-1">Reason for Cancellation</label>
                                <p className="mb-0 fw-medium text-dark">{cancellationData.cancellationReason || 'No reason provided.'}</p>
                            </div>

                            <div className="row g-2">
                                <div className="col-6">
                                    <div className="border p-2 rounded-3 text-start">
                                        <label className="text-xs text-secondary fw-bold text-uppercase mb-1">Reschedule Status</label>
                                        <div className="d-flex align-items-center gap-2">
                                            <Icon icon="solar:refresh-circle-bold" className={`text-xl ${cancellationData.rescheduleStatus === 'Completed' ? 'text-success' : 'text-warning'}`} />
                                            <span className="fw-bold text-sm">{cancellationData.rescheduleStatus || 'None'}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-6">
                                    <div className="border p-2 rounded-3 text-start">
                                        <label className="text-xs text-secondary fw-bold text-uppercase mb-1">Original Date</label>
                                        <div className="d-flex align-items-center gap-2">
                                            <Icon icon="solar:calendar-date-bold" className="text-primary text-xl" />
                                            <span className="fw-bold text-sm">{new Date(cancellationData.date).toLocaleDateString()}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0 justify-content-center">
                    <Button variant="light" onClick={() => setShowCancellationModal(false)} className="px-4">Close</Button>
                </Modal.Footer>
            </Modal>

            {/* PENDING PAYMENT DETAILS MODAL */}

        </section>
    );
};

export default AssistantDashboard;
